import { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useTranslation from '~/hooks/useTranslation';
import TextField from '~/components/Form/TextField';
import notesStyles from '~/styles/bookingFlow/notes.module.css';
import FormGroup from '~/components/Form/FormGroup';
import PlainCheckbox from '~/components/Form/PlainCheckbox';

type Props = {
  registerFieldPath: string;
};

export default function ParticipantNotes({
  registerFieldPath,
}: Props) {
  const translate = useTranslation();
  const { setValue } = useFormContext();
  const [medicalConditionsOpen, setMedicalConditionsOpen] = useState(false);
  const [dietaryRestrictionsOpen, setDietaryRestrictionsOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);

  const medicalInfo = useWatch({ name: `${registerFieldPath}.medical_info` });
  const mealPreferences = useWatch({ name: `${registerFieldPath}.meal_preferences` });
  const preferences = useWatch({ name: `${registerFieldPath}.preferences` });

  const onToggleMedicalInfo = useCallback((checked: boolean) => {
    setMedicalConditionsOpen(checked);

    if (!checked) {
      setValue(`${registerFieldPath}.medical_info`, '');
    }
  }, [registerFieldPath, setValue]);

  const onToggleDietaryRestrictions = useCallback((checked: boolean) => {
    setDietaryRestrictionsOpen(checked);

    if (!checked) {
      setValue(`${registerFieldPath}.meal_preferences`, '');
    }
  }, [registerFieldPath, setValue]);

  const onToggleRemarks = useCallback((checked: boolean) => {
    setRemarksOpen(checked);

    if (!checked) {
      setValue(`${registerFieldPath}.preferences`, '');
    }
  }, [registerFieldPath, setValue]);

  if (medicalInfo && !medicalConditionsOpen) {
    setMedicalConditionsOpen(true);
  }

  if (mealPreferences && !dietaryRestrictionsOpen) {
    setDietaryRestrictionsOpen(true);
  }

  if (preferences && !remarksOpen) {
    setRemarksOpen(true);
  }

  return (
    <>
      <FormGroup>
        <PlainCheckbox
          label={translate('bookingFlow.information.add_medical_conditions')}
          checked={medicalConditionsOpen}
          onChange={onToggleMedicalInfo}
        />

        {medicalConditionsOpen && (
          <div className={notesStyles.notesCollapse}>
            <TextField
              label={translate('bookingFlow.information.medical_conditions')}
              registerField={`${registerFieldPath}.medical_info`}
              rows={3}
            />
          </div>
        )}
      </FormGroup>

      <FormGroup>
        <PlainCheckbox
          label={translate('bookingFlow.information.add_dietary_restrictions')}
          checked={dietaryRestrictionsOpen}
          onChange={onToggleDietaryRestrictions}
        />

        {dietaryRestrictionsOpen && (
          <div className={notesStyles.notesCollapse}>
            <TextField
              label={translate('bookingFlow.information.dietary_restrictions')}
              registerField={`${registerFieldPath}.meal_preferences`}
              rows={3}
            />
          </div>
        )}
      </FormGroup>

      <FormGroup>
        <PlainCheckbox
          label={translate('bookingFlow.information.add_remarks')}
          checked={remarksOpen}
          onChange={onToggleRemarks}
        />

        {remarksOpen && (
          <div className={notesStyles.notesCollapse}>
            <TextField
              label={translate('bookingFlow.information.remarks')}
              registerField={`${registerFieldPath}.preferences`}
              rows={3}
            />
          </div>
        )}
      </FormGroup>
    </>
  );
}
